import React from 'react';
import { ContentCard } from '../types';

interface HeroSectionProps {
  featuredContent?: ContentCard;
  onPlayClick?: (content: ContentCard) => void;
  showPopularityIndicator?: boolean; // Show that this is from popular content
  isFromPopularContent?: boolean; // Indicates if content is from top 500 popular
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  featuredContent, 
  onPlayClick, 
  showPopularityIndicator = false,
  isFromPopularContent = false 
}) => {
  if (!featuredContent) {
    return (
      <div className="relative h-96 md:h-[70vh] bg-gray-800 animate-pulse">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8">
          <div className="max-w-2xl">
            <div className="h-8 bg-gray-700 rounded mb-4 w-3/4"></div>
            <div className="h-4 bg-gray-700 rounded mb-2 w-full"></div>
            <div className="h-4 bg-gray-700 rounded mb-6 w-2/3"></div>
            <div className="flex gap-4">
              <div className="h-12 bg-gray-700 rounded w-24"></div>
              <div className="h-12 bg-gray-700 rounded w-32"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handlePlayClick = () => {
    if (onPlayClick) {
      onPlayClick(featuredContent);
    }
  };

  return (
    <div className="relative h-96 md:h-[70vh] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={featuredContent.imageUrl}
          alt={featuredContent.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = '/placeholder-hero.jpg';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative h-full flex items-end">
        <div className="p-4 md:p-8 pb-8 md:pb-16 max-w-4xl">
          <div className="mb-4">
            {/* Popular content badge */}
            {isFromPopularContent && showPopularityIndicator && (
              <div className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold px-4 py-2 rounded-full mb-4 shadow-lg">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
                TRENDING NOW
              </div>
            )}
            
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {featuredContent.title}
            </h1>
          </div>
          
          <div className="flex items-center gap-4 mb-4 text-sm md:text-base">
            <span className="text-white font-medium">{featuredContent.year}</span>
            {featuredContent.rating && (
              <div className="flex items-center text-yellow-400">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-white">{featuredContent.rating.toFixed(1)}</span>
              </div>
            )}
            {/* Popularity score indicator */}
            {featuredContent.popularity !== undefined && showPopularityIndicator && (
              <div className="flex items-center text-orange-400">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
                <span className="text-white font-medium">{featuredContent.popularity.toFixed(1)} Popular</span>
              </div>
            )}
            <div className="flex gap-2">
              {featuredContent.genres.slice(0, 3).map((genre) => (
                <span key={genre} className="text-gray-300">
                  {genre}
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handlePlayClick}
              className="flex items-center justify-center gap-2 bg-white text-black font-semibold py-3 px-8 rounded-lg hover:bg-gray-200 transition-colors duration-200 focus-ring"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              Play
            </button>
            
            <button className="flex items-center justify-center gap-2 bg-gray-600/80 text-white font-semibold py-3 px-8 rounded-lg hover:bg-gray-600 transition-colors duration-200 focus-ring">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              More Info
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;